/**
 * Keeps every calling number on the storefront aligned to London opening hours.
 * Daytime is 10:00–17:59 Europe/London; 18:00 onward uses the after-hours mobile.
 */
(() => {
  const DAYTIME = {
    display: '+44 2079165682',
    href: 'tel:+442079165682',
  };
  const AFTER_HOURS = {
    display: '+44 7904273387',
    href: 'tel:+447904273387',
  };
  const UK_PHONE_PATTERN = /(?:\+?\s*44(?:\s*\(0\))?|0)(?:[\s().-]*\d){9,10}/g;
  const CALL_CONTEXT = /\b(call|calling|phone|telephone|ring us|support|sales manager|office)\b/i;
  const WHATSAPP_CONTEXT = /\bwhats?app\b/i;
  const londonClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    hourCycle: 'h23',
  });

  // A market with its own line (the US) sets it on <html>. That number is shown as-is, with no
  // opening-hours swap and no Google forwarding number, which only applies to the UK line.
  const marketPhone = (() => {
    const { amadaPhone, amadaPhoneHref } = document.documentElement.dataset;
    return amadaPhone && amadaPhoneHref ? { display: amadaPhone, href: amadaPhoneHref } : null;
  })();

  const currentPhone = () => {
    if (marketPhone) return marketPhone;
    const hourPart = londonClock.formatToParts(new Date()).find((part) => part.type === 'hour');
    const hour = Number(hourPart?.value);
    if (hour < 10 || hour >= 18) return AFTER_HOURS;
    // Google call tracking (amada-conversions.js) may supply a forwarding number for ad visitors.
    const tracked = window.AmadaPhoneOverride;
    return tracked && tracked.display && tracked.href ? tracked : DAYTIME;
  };

  const precedingContext = (node) => {
    let context = node.nodeValue || '';
    let sibling = node.previousSibling;

    while (sibling && context.length < 120) {
      if (sibling.nodeName === 'BR') break;
      context = `${sibling.textContent || ''} ${context}`;
      sibling = sibling.previousSibling;
    }

    return context;
  };

  const replaceCallingText = (root, phone) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const parent = node.parentElement;
      if (!parent || parent.closest('script, style, noscript, textarea')) return;
      if (!UK_PHONE_PATTERN.test(node.nodeValue || '')) {
        UK_PHONE_PATTERN.lastIndex = 0;
        return;
      }
      UK_PHONE_PATTERN.lastIndex = 0;

      const telephoneLink = parent.closest('a[href^="tel:"]');
      const context = precedingContext(node);
      const isWhatsAppOnly = !telephoneLink && WHATSAPP_CONTEXT.test(context);
      if (isWhatsAppOnly || (!telephoneLink && !CALL_CONTEXT.test(context))) return;

      node.nodeValue = node.nodeValue.replace(UK_PHONE_PATTERN, phone.display);
      UK_PHONE_PATTERN.lastIndex = 0;
    });
  };

  const applyPhone = (root = document.body) => {
    const phone = currentPhone();
    const scope = root.nodeType === Node.ELEMENT_NODE || root === document.body ? root : root.parentElement;
    if (!scope) return;

    const anchors = Array.from(scope.querySelectorAll('a[href^="tel:"]'));
    if (scope.matches && scope.matches('a[href^="tel:"]')) anchors.push(scope);

    anchors.forEach((anchor) => {
      anchor.setAttribute('href', phone.href);

      const label = anchor.getAttribute('aria-label');
      if (label && UK_PHONE_PATTERN.test(label)) {
        anchor.setAttribute('aria-label', label.replace(UK_PHONE_PATTERN, phone.display));
      }
      UK_PHONE_PATTERN.lastIndex = 0;
    });

    replaceCallingText(root, phone);
  };

  // Only the nodes a mutation added are rescanned, and never inside the
  // interaction's frame: a page-wide text walk after every menu, filter or
  // cart-drawer update was adding to mobile INP.
  const pending = new Set();
  let flushQueued = false;
  const flush = () => {
    flushQueued = false;
    const roots = Array.from(pending);
    pending.clear();
    roots.forEach((root) => {
      if (root.isConnected) applyPhone(root);
    });
  };
  const queueUpdate = (records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) pending.add(node);
      });
    });
    if (flushQueued || !pending.size) return;
    flushQueued = true;
    if ('requestIdleCallback' in window) window.requestIdleCallback(flush, { timeout: 1500 });
    else window.setTimeout(flush, 250);
  };

  applyPhone();

  new MutationObserver(queueUpdate).observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.setInterval(() => applyPhone(), 60_000);
  window.addEventListener('pageshow', () => applyPhone());
  document.addEventListener('amada:phone-override', () => applyPhone());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) applyPhone();
  });
})();
